import { useEffect } from 'react';
import Router from 'next/router';
import { checkBuildStale, isBuildStale } from '@/shared/services/buildVersion';

const pathnameOf = (url : string) : string => new URL(url, window.location.href).pathname;

const StaleBuildGuard = () : null => {
    useEffect(() => {
        const checkWhenVisible = () => {
            if (document.visibilityState === 'visible') void checkBuildStale();
        };

        const leaveStaleBuild = (url : string, { shallow } : { shallow : boolean }) => {
            // Query-only updates stay in place so filters and open forms keep their state.
            // The router still holds the page being left, also for back/forward where the address already changed.
            if (shallow || pathnameOf(url) === pathnameOf(Router.asPath)) return;

            if (!isBuildStale()) {
                void checkBuildStale();

                return;
            }

            // A full page load picks up the deployed bundle; Next.js aborts its own transition the same way.
            window.location.assign(url);

            throw Object.assign(new Error('Route cancelled: a newer build is deployed'), { cancelled: true });
        };

        // A document restored from the browser cache can be stale from its first moment.
        checkWhenVisible();
        document.addEventListener('visibilitychange', checkWhenVisible);
        Router.events.on('routeChangeStart', leaveStaleBuild);

        return () => {
            document.removeEventListener('visibilitychange', checkWhenVisible);
            Router.events.off('routeChangeStart', leaveStaleBuild);
        };
    }, []);

    return null;
};

export default StaleBuildGuard;
